<template>
  <ProgressModal
    v-if="isLoadingAction"
    title="Please wait..."
    :progressIndicator="progressIndicator"
    :processedItems="processedItems"
    :totalItems="campaignsSelected.length"
  />

  <div
    v-if="!rain_id"
    id="campaigns-table"
    :class="`content-wrapper animation-slide ${
      isMenuOpen ? 'menu-open' : 'menu-closed'
    }`"
  >
    <div class="content content-styles text-white ov-visible md:mx-[1rem]">
      <div
        class="text-white box content-box-style large ov-hidd p-0 min-h-[100dvh] space-y-3"
      >
        <Filters
          :options="options"
          :showActionButton="true"
          :search="search"
          :resetFilter="resetFilter"
          ><div class="ms-auto pl-2">
            <router-link
              :to="{
                name: 'RainsCreate',
                params: {
                  lang: $route.params.lang || 'en',
                  ...($route.params.skinFilt || skinStorage ? { skinFilt: ($route.params.skinFilt || skinStorage).toString() } : {}),
                },
              }"
              class="text-teal-900 btn bg-teal-100 hover:bg-teal-200 inline-flex items-center gap-2 rounded-xl px-4 py-2 transition-colors duration-150 active:scale-95 border border-teal-200"
            >
              <i class="fa-solid fa-tint text-lg text-teal-600"></i>
              <span class="text font-bold text-teal-900 tracking-tight">{{
                $t("util.create")
              }}</span>
            </router-link>
          </div></Filters
        >

        <Transition name="fade-slide">
          <div
            v-if="campaignsSelected.length > 0"
            class="manage-freebets-status mt-3"
          >
            <div class="flex items-center gap-2">
              <button
                v-for="(btn, index) in rainsButton"
                :key="index"
                @click="btn.action()"
                :class="[
                  'inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm transition-all duration-200 active:scale-95 shadow-sm whitespace-nowrap',
                  btn.name === 'Enable'
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
                    : '',
                  btn.name === 'Disable'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200'
                    : '',
                  btn.name === 'Remove'
                    ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-200'
                    : '',
                ]"
              >
                <span>{{ btn.name }}</span>
                <span class="opacity-60 text-xs"
                  >({{ campaignsSelected.length }})</span
                >
                <i :class="`fas fa-${btn.icon} text-xs`"></i>
              </button>
            </div>
          </div>
        </Transition>

        <div
          v-if="isLoading"
          class="flex-grow flex flex-col items-center justify-center p-20"
        >
          <Loader />
        </div>

        <div
          v-else
          class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3"
        >
          <DataTable
            v-model:selection="campaignsSelected"
            :value="tableData"
            dataKey="_id"
            :lazy="true"
            :paginator="true"
            :rows="selectedLimit"
            :totalRecords="totalElements"
            :first="(currentPage - 1) * selectedLimit"
            :rowsPerPageOptions="[15, 25, 50, 100]"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :currentPageReportTemplate="`Showing {first} to {last} of {totalRecords} campaigns`"
            showCurrentPageReport
            @page="onPage"
            @sort="onSort"
            :sortField="sortField"
            :sortOrder="sortOrder"
            @row-select="onRowSelect"
            @row-unselect="onRowUnselect"
            @row-select-all="onRowSelectAll"
            @row-unselect-all="onRowUnselectAll"
            removableSort
            stripedRows
            scrollable
            scrollHeight="flex"
            responsiveLayout="stack"
            class="rains-datatable"
          >
            <template #empty>
              <div class="text-center py-4 text-muted">No rains found.</div>
            </template>
            <Column selectionMode="multiple" headerStyle="width: 1rem" />

            <Column field="_id" header="ID" class="small"></Column>

            <Column field="title" header="Title" sortable>
              <template #body="{ data }">
                <div class="flex flex-col gap-1 py-1">
                  <span class="font-bold text-slate-800">{{ data.title }}</span>
                  <div>
                    <span
                      v-if="data.is_mistery"
                      class="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200"
                    >
                      <i class="fa-solid fa-gift text-[10px]"></i> Mystery Bet Drop
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200"
                    >
                      <i class="fa-solid fa-cloud-showers-heavy text-[10px]"></i> Chat Rain
                    </span>
                  </div>
                </div>
              </template>
            </Column>

            <Column field="skin_id" header="Skin" sortable></Column>

            <Column field="is_enabled" header="Status" sortable>
              <template #body="{ data }">
                <span :class="data.is_enabled ? 'text-success' : 'text-danger'">
                  <i
                    :class="`fas fa-${
                      data.is_enabled ? 'check-circle' : 'times-circle'
                    }`"
                  ></i>
                  {{ data.is_enabled ? "Enabled" : "Disabled" }}
                </span>
              </template>
            </Column>

            <Column field="total_rain" header="Total Rain" sortable></Column>

            <Column header="Schedule">
              <template #body="{ data }">
                <div class="small text-nowrap">
                  <div>
                    <i class="far fa-calendar-alt me-1"></i>
                    {{ formatDate(data.start_date) }}
                  </div>
                  <div class="text-muted">
                    <i class="far fa-clock me-1"></i>
                    {{ formatTime(data.start_time) }} -
                    {{ formatTime(data.end_time) }}
                  </div>
                </div>
              </template>
            </Column>

            <Column
              header="Actions"
              :exportable="false"
              style="min-width: 150px"
            >
              <template #body="{ data }">
                <div class="flex items-center gap-1">
                  <router-link
                    :to="{
                      name: 'Rains',
                      params: {
                        id: data._id,
                        lang: $route.params.lang || 'en',
                        ...($route.params.skinFilt || skinStorage || data.skin_id
                          ? {
                              skinFilt: (
                                $route.params.skinFilt ||
                                skinStorage ||
                                data.skin_id
                              ).toString(),
                            }
                          : {}),
                      },
                    }"
                  >
                    <Button
                      icon="pi pi-eye"
                      rounded
                      text
                      severity="info"
                      v-tooltip.top="'View Detail'"
                      class="hover:bg-blue-50 rounded-full"
                    />
                  </router-link>
                  <router-link
                    :to="{
                      name: 'RainsEdit',
                      params: {
                        id: data._id,
                        lang: $route.params.lang || 'en',
                        ...($route.params.skinFilt || skinStorage || data.skin_id
                          ? {
                              skinFilt: (
                                $route.params.skinFilt ||
                                skinStorage ||
                                data.skin_id
                              ).toString(),
                            }
                          : {}),
                      },
                    }"
                  >
                    <Button
                      icon="pi pi-pencil"
                      rounded
                      text
                      severity="warning"
                      v-tooltip.top="'Edit Rain'"
                      class="hover:bg-amber-50 rounded-full"
                    />
                  </router-link>
                  <Button
                    icon="pi pi-trash"
                    rounded
                    text
                    severity="danger"
                    v-tooltip.top="'Remove Rain'"
                    @click="removeRainRow(data)"
                    class="hover:bg-red-50 rounded-full"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </div>

  <div
    v-else
    id="freebet-table-details"
    class="h-auto"
    :class="`content-wrapper ${isMenuOpen ? 'menu-open' : 'menu-closed'}`"
  >
   
      <div
        class="flex items-center justify-start mb-3 md:px-3"
      >
      <div
        class="inline-flex p-1.5 bg-gray-100/80 rounded-2xl border border-gray-200/50 shadow-inner backdrop-blur-md overflow-x-auto"
      >
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :id="`tab-${tab.value}`"
          @click="tabSelected = tab.value"
          :class="[
            'relative px-3 py-2.5 text-[11px] whitespace-nowrap font-semibold uppercase tracking-widest transition-all duration-300 rounded-xl flex items-center gap-2 group',
            tabSelected === tab.value
              ? 'bg-white text-[#386f99] shadow-[0_4px_12px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.03]'
              : 'text-slate-500 hover:text-slate-700 hover:bg-white/50',
          ]"
        >
          <div class="relative flex items-center justify-center">
            <span
              v-if="tabSelected === tab.value"
              class="absolute h-2 w-2 animate-pulse rounded-full bg-[#386f99]/30"
            ></span>
            <span
              :class="[
                'relative rounded-full transition-all duration-300',
                tabSelected === tab.value
                  ? 'h-1.5 w-1.5 bg-[#386f99] scale-110'
                  : 'h-1 w-1 bg-gray-300 group-hover:bg-gray-400',
              ]"
            ></span>
          </div>

          <span class="relative">{{ tab.label }}</span>
        </button>
      </div>
      </div>
    

    <RainInfo
      v-if="tabSelected === 'rains_detail'"
      :isLoadingDetail="isLoading"
      :detail="detail"
      :campaignId="rain_id"
      :saveDetail="saveDetail"
    />
    <GeneratedRain
      v-if="tabSelected === 'rains_generated'"
      :campaignId="rain_id"
      :skinFilt="rain_skin_id"
    />
    <RainPlayerActive
      v-if="tabSelected === 'player_active'"
      :campaignId="rain_id"
      :skinFilt="rain_skin_id"
    />
    <RoundFinder
      v-if="tabSelected === 'rounds_info'"
      :skinIdProps="rain_skin_id"
      :fieldToExclude="['campaign_id']"
      :options="filters"
       :campaignIdProps="rain_id"
    />
    <RainRestrictions
      v-if="tabSelected === 'rain_restricted'"
      :freebet_id="rain_id"
      :skinFilt="rain_skin_id"
      :detail="detail"
    />
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import { getFetch, putFetch, deleteRequest } from "@/api/axiosClient";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";

// Components
import Loader from "../components/Loader.vue";
import Filters from "../components/table/Filters.vue";
import ProgressModal from "../components/Modal/ProgressModal.vue";
import RainInfo from "../components/RainComponent/RainInfo.vue";
import RainPlayerActive from "../components/RainComponent/RainPlayerActive.vue";
import GeneratedRain from "../components/RainComponent/GeneratedRain.vue";
import RainRestrictions from "../components/RainComponent/RainRestrictions.vue";
import RoundFinder from "../components/round/RoundFinder.vue";

export default {
  name: "CampaignPage",
  components: {
    DataTable,
    Column,
    Button,
    ProgressModal,
    Loader,
    Filters,
    RainInfo,
    RainPlayerActive,
    RoundFinder,
    GeneratedRain,
    RainRestrictions,
  },
  data() {
    return {
      tableData: [],
      campaignsSelected: [],
      isLoading: false,
      isLoadingAction: false,

      totalElements: 0,
      totalPages: 1,
      currentPage: 1,
      selectedLimit: 50,
      sortField: "start_date",
      sortOrder: -1,

      _id: "",
      start_date: null,
      end_date: null,
      campaign_type: "All",
      is_enabled: "All",
      has_rain: "All",
      skinListAdmin: "All",
      is_mistery: "All",

      tabSelected: "rains_detail",
      tabs: [
        { label: "Details", value: "rains_detail" },
        { label: "Rains", value: "rains_generated" },
        { label: "Players Active", value: "player_active" },
        { label: "Rounds", value: "rounds_info" },
        { label: "Restrictions", value: "rain_restricted" },
      ],
      detail: {},
      rain_skin_id: "",

      options: [
        {
          label: "ID",
          type: "text",
          property: "_id",
          placeholder: "Search ID",
        },
        {
          label: "Type",
          type: "select",
          property: "is_mistery",
          options: ["All", "Chat Rain", "Mystery Bet Drop"],
        },
        {
          label: "Start Date",
          type: "datetime-local",
          property: "start_date",
          isDateTime: true,
        },
        {
          label: "End Date",
          type: "datetime-local",
          property: "end_date",
          isDateTime: true,
        },
        {
          label: "Has Rain",
          type: "select",
          property: "has_rain",
          options: ["All", "True", "False"],
        },
        {
          label: "Enabled",
          type: "select",
          property: "is_enabled",
          options: ["All", "True", "False"],
        },
      ],

      rainsButton: [
        {
          name: "Enable",
          icon: "circle-check",
          action: () => this.changeStatus("enable"),
        },
        {
          name: "Disable",
          icon: "circle-xmark",
          action: () => this.changeStatus("disable"),
        },
        {
          name: "Remove",
          icon: "trash",
          action: () => this.removeRainCampaign(),
        },
      ],
      progressIndicator: 0,
      processedItems: 0,
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen", "isAdmin", "allowedSkins"]),
    rain_id() {
      return this.$route.params.id;
    },
  },
  watch: {
    rain_id: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.fetchRainDetails();
        else this.search();
      },
    },
    allowedSkins: {
      immediate: true,
      handler(newVal) {
        if (Array.isArray(newVal)) this.initSkinFilter();
      },
    },
  },
  methods: {
    initSkinFilter() {
      if (this.isAdmin) {
        const skins = ["All", ...this.allowedSkins.map((s) => s.ref_id)];
        const existing = this.options.find(
          (o) => o.property === "skinListAdmin",
        );
        if (!existing) {
          this.options.push({
            label: "Skin ID",
            type: "select",
            property: "skinListAdmin",
            options: skins,
          });
        } else {
          existing.options = skins;
        }
      }
    },

    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search();
    },

    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.search();
    },

    onRowSelect() {},
    onRowUnselect() {},
    onRowSelectAll() {},
    onRowUnselectAll() {},

    async search() {
      this.isLoading = true;
      const params = {
        page: this.currentPage,
        limit: this.selectedLimit,
        mode: "rain",
        sortField: this.sortField,
        sortOrder: this.sortOrder === -1 ? "desc" : "asc",
      };

      if (this.start_date) params.start_date = new Date(this.start_date + "Z");
      if (this.end_date) params.end_date = new Date(this.end_date + "Z");
      if (this._id) params.id = this._id.trim();
      if (this.is_enabled !== "All")
        params.is_enabled = this.is_enabled === "True";
      if (this.has_rain !== "All") params.has_rain = this.has_rain === "True";
      if (this.is_mistery && this.is_mistery !== "All")
        params.is_mistery = this.is_mistery === "Mystery Bet Drop";
      if (this.isAdmin && this.skinListAdmin !== "All")
        params.skin_id = parseInt(this.skinListAdmin);

      try {
        const response = await getFetch(
          "/campaigns",
          params,
          this.skinListAdmin === "All",
        );
        this.tableData = response.data || [];
        this.totalElements = response.totalItems || 0;
        this.totalPages = response.totalPages || 1;
      } catch (error) {
        console.error("Search Error:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchRainDetails() {
      this.isLoading = true;
      try {
        const data = await getFetch(`campaigns/${this.rain_id}`);
        this.detail = data;
        this.saveDetail(data);
      } catch (e) {
        this.$router.push("/404");
      } finally {
        this.isLoading = false;
      }
    },

    async removeRainRow(row) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        try {
          await deleteRequest(`campaigns/${row._id}`);
          Swal.fire("Deleted!", "Campaign has been deleted.", "success");
          this.search();
        } catch (e) {
          Swal.fire("Error", "Delete failed", "error");
        }
      }
    },

    async removeRainCampaign() {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `You are about to delete ${this.campaignsSelected.length} campaigns!`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete them!",
      });

      if (result.isConfirmed) {
        this.isLoadingAction = true;
        try {
          for (let i = 0; i < this.campaignsSelected.length; i++) {
            await deleteRequest(`campaigns/${this.campaignsSelected[i]._id}`);
            this.processedItems = i + 1;
            this.progressIndicator = Math.round(
              ((i + 1) / this.campaignsSelected.length) * 100,
            );
          }
          Swal.fire("Deleted!", "Campaigns deleted successfully.", "success");
          this.campaignsSelected = [];
          this.search();
        } catch (e) {
          Swal.fire("Error", "Bulk delete failed", "error");
        } finally {
          this.isLoadingAction = false;
        }
      }
    },

    async changeStatus(action) {
      const confirm = await Swal.fire({
        title: `Confirm ${action}?`,
        text: `Update status for ${this.campaignsSelected.length} items?`,
        icon: "question",
        showCancelButton: true,
      });

      if (!confirm.isConfirmed) return;

      this.isLoadingAction = true;
      this.progressIndicator = 0;
      try {
        for (let i = 0; i < this.campaignsSelected.length; i++) {
          await putFetch(`/campaigns/${this.campaignsSelected[i]._id}`, {
            is_enabled: action === "enable",
          });
          this.processedItems = i + 1;
          this.progressIndicator = Math.round(
            ((i + 1) / this.campaignsSelected.length) * 100,
          );
        }
        Swal.fire("Success", "Items updated successfully", "success");
        this.campaignsSelected = [];
        this.search();
      } catch (e) {
        Swal.fire("Error", "Action failed", "error");
      } finally {
        this.isLoadingAction = false;
      }
    },

    saveDetail(data) {
      this.detail = data;
      this.rain_skin_id = data.skin_id;
    },

    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}/${d.getFullYear()}`;
    },

    formatTime(v) {
      if (!v) return "-";
      if (typeof v === "string" && v.includes(":")) return v.substring(0, 5);
      const d = new Date(v);
      return `${String(d.getHours()).padStart(2, "0")}:${String(
        d.getMinutes(),
      ).padStart(2, "0")}`;
    },

    resetFilter() {
      this._id = "";
      this.start_date = null;
      this.end_date = null;
      this.is_enabled = "All";
      this.has_rain = "All";
      this.skinListAdmin = "All";
      this.currentPage = 1;
      this.search();
    },
  },
  mounted() {
    this.initSkinFilter();
  },
};
</script>
